import { Loader2 } from "lucide-react";
import type { CarouselLlmProgress } from "@/lib/api";
import { estimateLlmProgress, STEP_BAR } from "@/lib/llmProgress";
import { clockLabel, useNowTick } from "@/lib/useNowTick";
import { cn } from "@/lib/utils";

function k(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function eventLabel(event?: string): string {
  if (event === "alive") return "czekam na znak";
  if (event === "prompt_ready") return "gotowe zapytanie";
  if (event === "call") return "wysyłam do modelu";
  if (event === "retry") return "ponawiam strzał";
  if (event === "first_token") return "leci tekst";
  if (event === "response") return "koniec strzału";
  if (event === "orphaned") return "job zamknięty (timeout)";
  if (event === "error") return "błąd strzału";
  return event || "";
}

export default function LlmReceiveProgress({
  progress,
  className,
  fallbackStartedAt,
  llmStatus,
}: {
  progress?: CarouselLlmProgress | null;
  className?: string;
  fallbackStartedAt?: string | null;
  llmStatus?: string;
}) {
  const startedAt = progress?.started_at || fallbackStartedAt || "";
  const rawPhase = progress?.phase || (startedAt ? "waiting" : "");
  const idleDone = llmStatus === "idle" || llmStatus === "error";
  const phase = idleDone && rawPhase !== "done" ? "done" : rawPhase;
  const running = Boolean(startedAt) && phase !== "done";
  const now = useNowTick(running);
  if (!startedAt && !progress?.phase) return null;
  const live = estimateLlmProgress(
    { ...(progress || {}), started_at: startedAt, phase: phase || "waiting" },
    now,
    llmStatus,
  );
  const pct = phase === "done" ? 100 : live.percent;
  const received = Number(progress?.received_chars || 0);
  const expected = Number(progress?.expected_chars || 0);
  const rate = Number(progress?.chars_per_sec || 0);
  const samples = Number(progress?.samples || 0);
  const expectedSec = Math.max(
    0,
    Math.round(Number(progress?.job_expected_ms || progress?.expected_ms || 0) / 1000),
  );
  const prompt = String(progress?.prompt || "");
  const promptChars = Number(progress?.prompt_chars || prompt.length || 0);
  const model = String(progress?.model || "");
  const waiting = phase === "waiting" || received <= 0;
  const limit = Number(progress?.awaiting_text_limit_sec || 60);
  const waitText = waiting ? live.awaiting_text_sec : 0;
  const overdue = waiting && phase !== "done" && waitText >= limit;
  const zombie = waiting && phase !== "done" && waitText >= Math.max(90, limit * 2);
  const segments = live.segments;
  const current = progress?.step || "";
  const currentLabel = STEP_BAR[current]?.label || current;
  const multi = segments.length > 1;
  const idx = multi ? segments.findIndex((row) => row.id === current) + 1 : 0;
  const title =
    phase === "done"
      ? llmStatus === "error"
        ? "Job zakończony błędem"
        : "Odebrano odpowiedź"
      : zombie
        ? `Brak znaku ${waitText} s · rozstrzygnięcie`
        : waiting
          ? waitText
            ? `Czekam na pierwszy znak ${waitText}/${limit} s`
            : "Czekam na pierwszy znak"
          : "Odbiór treści";
  return (
    <div className={cn("rounded-md border border-border bg-muted/30 p-3", className)} aria-live="polite">
      <div className="mb-1.5 flex items-center justify-between gap-2 text-xs">
        <span className="flex items-center gap-1.5 font-medium">
          {phase !== "done" ? <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-700" /> : null}
          {multi && phase !== "done" ? `${idx}/${segments.length} ${currentLabel} · ` : null}
          {title}
        </span>
        <span className="tabular-nums text-muted-foreground">{pct}%</span>
      </div>
      {multi ? (
        <div className="flex h-3 gap-1">
          {segments.map((seg) => {
            const meta = STEP_BAR[seg.id || ""] || { bar: "bg-cyan-500", track: "bg-cyan-200" };
            return (
              <div
                key={seg.id}
                className={cn("relative h-full min-w-0 flex-1 overflow-hidden rounded-sm", meta.track)}
              >
                <div
                  className={cn("absolute inset-y-0 left-0 transition-all", meta.bar)}
                  style={{ width: `${Math.round(Number(seg.fraction || 0) * 100)}%` }}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div
            className={cn("h-full transition-all", zombie ? "bg-amber-600" : "bg-cyan-600")}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {multi ? (
        <div className="mt-1 flex gap-0.5 text-[10px] text-muted-foreground">
          {segments.map((seg) => (
            <span
              key={`l-${seg.id}`}
              className={cn("min-w-0 flex-1 truncate", seg.id === current ? "font-medium text-foreground" : "")}
            >
              {seg.label}
            </span>
          ))}
        </div>
      ) : null}
      <p className="mt-2 text-xs text-muted-foreground">
        minęło {clockLabel(live.elapsed_sec)}
        {phase !== "done" ? ` · zostało ok. ${clockLabel(live.eta_sec)}` : ""}
        {expectedSec > 0 && phase !== "done" ? ` · średnio ~${clockLabel(expectedSec)}` : ""}
        {received > 0 ? ` · ${k(received)} / ~${k(expected)} znaków` : ""}
        {rate > 0 ? ` · ${rate} zn/s` : ""}
        {model ? ` · ${model}` : ""}
        {progress?.last_event ? ` · ${eventLabel(progress.last_event)}` : ""}
        {samples > 0
          ? ` · średnia z ${samples} ${samples === 1 ? "generacji" : "generacji"}`
          : " · pierwsza generacja, szacunek startowy"}
      </p>
      {waiting && phase !== "done" ? (
        <p
          className={cn(
            "mt-1 text-xs",
            overdue ? "font-medium text-amber-800" : waitText >= Math.max(8, limit * 0.7) ? "text-warning" : "text-muted-foreground",
          )}
        >
          {zombie
            ? `Brakuje: pierwszego znaku z modelu${model ? ` (${model})` : ""}. Minęło ${waitText} s przy limicie ${limit} s (3× typowy). Worker powinien przerwać albo poll zaraz zamknie job jako błąd. Potem: Generuj treść ponownie albo wpisz ręcznie.`
            : overdue
              ? `Limit ${limit} s bez tekstu (3× typowy). Brakuje pierwszego tokenu z API${model ? ` · ${model}` : ""}. Worker ponawia strzał (max 2), potem przerywa.`
              : `Model myśli albo łączy się z API. To nie ponowienie. Bez znaku max ${limit} s (3× typowy czas).`}
        </p>
      ) : null}
      {progress?.hint ? <p className="mt-1 text-xs text-warning">{progress.hint}</p> : null}
      {prompt ? (
        <details className="mt-2 rounded-sm border border-border bg-background/70 p-2">
          <summary className="cursor-pointer text-xs font-medium">
            Zapytanie do modelu
            {promptChars ? ` · ${k(promptChars)} zn` : ""}
          </summary>
          <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-snug text-foreground">
            {prompt}
          </pre>
        </details>
      ) : running && phase !== "done" ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {progress?.last_event === "call" || progress?.last_event === "alive" || progress?.last_event === "retry"
            ? "Zapytanie poszło do modelu. Czekam na stream / pierwszy znak…"
            : "Składam zapytanie do modelu…"}
        </p>
      ) : null}
    </div>
  );
}
